import './index.scss'
import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { setLoginShow } from '@/redux/modules/user/action.js'
import { message } from 'antd'
import {
  EyeOutlined,
  MessageOutlined,
  LikeOutlined,
  StarOutlined,
  ShareAltOutlined,
  CloudDownloadOutlined,
  StarFilled,
  LikeFilled
} from '@ant-design/icons'
import { shareCountAdd } from '@/api/api'
import { collectModel, thumbModel } from '@/api'
import { formatNumber } from '@/utils/format'

const ModelAction = ({ user, actionType, model, callback, modelUserInfo, readonly }) => {

  const dispatch = useDispatch()
  const [messageApi, contextHolder] = message.useMessage()
  
  // 本地状态管理（用于即时反馈）
  const [isLiked, setIsLiked] = useState(false)
  const [isCollected, setIsCollected] = useState(false)
  const [isShared, setIsShared] = useState(false)
  const [clickAnimation, setClickAnimation] = useState({ like: false, collect: false, share: false })
  
  // 根据modelUserInfo初始化状态
  useEffect(() => {
    if (modelUserInfo) {
      setIsLiked(modelUserInfo.isPraise || false)
      setIsCollected(modelUserInfo.isCollection || false)
    }
  }, [modelUserInfo])

  // 点赞
  const handleLike = async (e) => {
    e.stopPropagation()
    if (readonly) {
      return
    }
    if (!user || !user.isLogin) {
      dispatch(setLoginShow(true))
      return
    }
    if (isLiked) {
      message.warning('已经点过赞了')
      return
    }
    
    // 即时反馈：先更新UI状态，再调用API
    setIsLiked(true)
    setClickAnimation({ ...clickAnimation, like: true })
    setTimeout(() => setClickAnimation({ ...clickAnimation, like: false }), 300)
    
    try {
      const res = await thumbModel({
        modelId: model.id,
      })
      if (res.msg === 'success') {
        messageApi.success('已点赞')
        callback('点赞')
      } else {
        // API失败时回滚状态
        setIsLiked(false)
        messageApi.error('点赞失败，请重试')
      }
    } catch (e) {
      console.log(e)
      // API失败时回滚状态
      setIsLiked(false)
      messageApi.error('点赞失败，请重试')
    }
  }

  // 收藏
  const handleCollect = async (e) => {
    e.stopPropagation()
    if (readonly) {
      return
    }
    if (!user || !user.isLogin) {
      dispatch(setLoginShow(true))
      return
    }

    if (isCollected) {
      message.warning('已经收藏过了')
      return
    }

    // 即时反馈：先更新UI状态，再调用API
    setIsCollected(true)
    setClickAnimation({ ...clickAnimation, collect: true })
    setTimeout(() => setClickAnimation({ ...clickAnimation, collect: false }), 300)

    try {
      const res = await collectModel({
        modelId: model.id
      })
      if (res.code === 0) {
        message.success('已收藏')
        callback('收藏')
      } else {
        // API失败时回滚状态
        setIsCollected(false)
        message.error('收藏失败，请重试')
      }
    } catch (e) {
      console.log(e)
      // API失败时回滚状态
      setIsCollected(false)
      message.error('收藏失败，请重试')
    }
  }

  // 分享
  const handleShare = async (e) => {
    e.stopPropagation()
    if (readonly) {
      return
    }
    if (!user || !user.isLogin) {
      dispatch(setLoginShow(true))
      return
    }
    
    // 即时反馈：显示点击效果
    setClickAnimation({ ...clickAnimation, share: true })
    setTimeout(() => setClickAnimation({ ...clickAnimation, share: false }), 300)
    
    try {
      // 先复制链接，再调用API（避免API失败影响复制）
      const url = window.location.href
      
      try {
        await navigator.clipboard.writeText(url)
        messageApi.success('已复制模型链接')
        setIsShared(true)
        callback('分享')
      } catch (copyErr) {
        console.error('复制失败:', copyErr)
        messageApi.error('复制失败，请手动复制链接')
        return // 复制失败就不调用API了
      }
      
      // 复制成功后再调用API记录分享次数（不阻塞用户操作）
      try {
        await shareCountAdd({
          modelId: model.id
        })
      } catch (apiErr) {
        // API失败不影响用户，静默处理
        console.log('分享统计API调用失败:', apiErr)
      }
    } catch (e) {
      console.log('分享功能异常:', e)
      messageApi.error('分享功能异常，请重试')
    }
    
  }

  return (
    <>
      {contextHolder}
      <div className="model-action">
        {
          actionType.includes('浏览')
            ? <div className="action-item" title={`浏览 ${model.modelViewNum || 0}`} style={{ cursor: 'default' }}>
              <EyeOutlined />
              <span>{formatNumber(model.modelViewNum || 0)}</span>
            </div>
            : null
        }
        {
          actionType.includes('评论')
            ? <div className="action-item" title={`评论 ${model.modelCommentNum || 0}`} style={{ cursor: 'default' }}>
              <MessageOutlined />
              <span>{formatNumber(model.modelCommentNum || 0)}</span>
            </div>
            : null
        }
        {
          actionType.includes('点赞')
            ? <div 
                className={`action-item ${isLiked ? 'action-item-active' : ''} ${clickAnimation.like ? 'action-item-click' : ''}`} 
                title={`点赞 ${model.modelPraiseNum || 0}`} 
                onClick={handleLike}
              >
              { isLiked ? <LikeFilled className="action-icon-filled" /> : <LikeOutlined /> }
              <span>{formatNumber(model.modelPraiseNum || 0)}</span>
            </div>
            : null
        }
        {
          actionType.includes('收藏')
            ? <div 
                className={`action-item ${isCollected ? 'action-item-active' : ''} ${clickAnimation.collect ? 'action-item-click' : ''}`} 
                title={`收藏 ${model.modelCollectNum || 0}`} 
                onClick={handleCollect}
              >
              { isCollected ? <StarFilled className="action-icon-filled" /> : <StarOutlined /> }
              <span>{formatNumber(model.modelCollectNum || 0)}</span>
            </div>
            : null
        }
        {
          actionType.includes('分享')
            ? <div 
                className={`action-item ${clickAnimation.share ? 'action-item-click' : ''}`} 
                title={`分享 ${model.modelShareNum || 0}`} 
                onClick={handleShare}
              >
              <ShareAltOutlined />
              <span>{formatNumber(model.modelShareNum || 0)}</span>
            </div>
            : null
        }
        {
          actionType.includes('下载')
            ? <div className="action-item" title={`下载 ${model.modelDownloadNum || 0}`} style={{ cursor: 'default' }}>
              <CloudDownloadOutlined />
              <span>{formatNumber(model.modelDownloadNum || 0)}</span>
            </div>
            : null
        }
      </div>
    </>
  )
}

export default ModelAction
